/**
 * API Client
 *
 * Axios instance configuration with interceptors for authentication,
 * request/response logging, and error handling.
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import i18n from '@/i18n/config'
import { logger } from '@/utils/logger'
import { ExtendedAxiosRequestConfig, ApiResponse, ApiErrorPayload } from './types'

/**
 * List of paths that should be excluded from logging
 * (health checks, probes, internal traffic)
 */
const HEALTH_CHECK_PATHS = [
  '/health',
  '/healthz',
  '/ready',
  '/readyz',
  '/live',
  '/liveness',
  '/status',
  '/ping',
  '/metrics',
  '/favicon.ico',
  '/robots.txt'
]

/**
 * User agents associated with internal/infrastructure traffic
 */
const INTERNAL_USER_AGENTS = [
  'kube-probe',
  'kubectl',
  'kubernetes',
  'docker',
  'containerd',
  'cadvisor',
  'prometheus',
  'grafana'
]

/**
 * API Client class with configured Axios instance
 */
class ApiClient {
  private client: AxiosInstance

  // #472 Slice 1 — Single in-flight token-refresh promise so concurrent
  // 401s (e.g. an onboarding submit that fans out into 3 requests with a
  // stale `token` cookie) coalesce into one refresh round-trip instead of
  // racing each other and invalidating the brand-new refresh token.
  private refreshInFlight: Promise<boolean> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: '/api/v1',
      timeout: 10000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  /**
   * #472 Slice 1 — single-flight refresh-then-retry helper.
   *
   * Reason: onboarding forms persist drafts to sessionStorage so an
   * applicant can come back the next day to finish; meanwhile the
   * `token` JWT cookie expires after 24h and submission fails with
   * 401 "Access token required". The refresh-token cookie is valid
   * for 7d so a silent refresh-and-retry rescues the submission
   * instead of forcing the applicant to re-login (and lose the
   * draft).
   *
   * Returns true if refresh succeeded and the caller should retry
   * the original request, false otherwise.
   */
  private async tryRefresh(): Promise<boolean> {
    if (!this.refreshInFlight) {
      // Use raw axios (NOT this.client) so the call never re-enters
      // this interceptor and infinite-loops on its own 401.
      this.refreshInFlight = axios
        .post(
          '/api/v1/users/auth/refresh',
          {},
          { withCredentials: true, timeout: 10_000 },
        )
        .then(() => {
          // #788 H2 — auth is cookie-only (httpOnly + secure + sameSite:strict).
          // Do NOT mirror access/refresh tokens into web storage: AuthContext
          // seeds from authApi.getProfile() (the cookie), never from localStorage,
          // so the mirror added nothing but an XSS-exfiltratable 7-day refresh
          // token. The backend has already cookie-rotated on success.
          return true
        })
        .catch(() => false)
        .finally(() => { this.refreshInFlight = null })
    }
    return this.refreshInFlight
  }

  /**
   * Configure request interceptor for auth tokens and logging
   */
  private setupRequestInterceptor(): void {
    this.client.interceptors.request.use(
      (config) => {
        // Handle FormData - let axios set Content-Type with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }

        // Generate request ID for tracing
        const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        config.headers['X-Request-ID'] = requestId
        ;(config as ExtendedAxiosRequestConfig).metadata = {
          requestId,
          startTime: Date.now()
        }

        // Log request (except health checks)
        if (!this.shouldIgnoreRequest(config)) {
          logger.logRequest(
            config.method?.toUpperCase() || 'GET',
            config.url || '',
            requestId,
            {
              baseURL: config.baseURL,
              headers: {
                'content-type': config.headers['Content-Type'],
              }
            }
          )
        }

        return config
      },
      (error) => {
        logger.logError('REQUEST', 'Unknown', error, 0)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Configure response interceptor for error handling and logging
   */
  private setupResponseInterceptor(): void {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        const requestId = (response.config as ExtendedAxiosRequestConfig).metadata?.requestId
        const startTime = (response.config as ExtendedAxiosRequestConfig).metadata?.startTime || Date.now()
        const duration = Date.now() - startTime

        // Log successful response (except health checks)
        if (!this.shouldIgnoreRequest(response.config)) {
          logger.logResponse(
            response.config.method?.toUpperCase() || 'GET',
            response.config.url || '',
            response.status,
            duration,
            requestId,
            {
              contentLength: response.headers['content-length'],
              contentType: response.headers['content-type'],
            }
          )
        }

        return response.data
      },
      async (error) => {
        const cfg = error.config as ExtendedAxiosRequestConfig | undefined
        const requestId = cfg?.metadata?.requestId
        const startTime = cfg?.metadata?.startTime || Date.now()
        const duration = Date.now() - startTime

        // #472 Slice 1 — silent refresh-then-retry on 401.
        //
        // Onboarding submissions (and any other long-lived flow) get a
        // free retry: fire /users/auth/refresh once, on success replay
        // the original request. Guards:
        //   - skip retries themselves (don't loop forever)
        //   - skip the refresh endpoint itself
        //   - skip the auth endpoints (login/logout/register) where
        //     401 is the legitimate response and shouldn't trigger a
        //     refresh attempt
        // On refresh failure, fall through to the existing
        // handleErrorResponse(cookie wipe + redirect for non-public).
        const url = cfg?.url || ''
        const isRefreshableTarget = !/\/users\/auth\/(refresh|login|logout|register|oauth)/.test(url)
        if (
          error.response?.status === 401 &&
          cfg &&
          !(cfg as any)._authRetry &&
          isRefreshableTarget
        ) {
          ;(cfg as any)._authRetry = true
          // Replay the original request once, whether or not our refresh
          // succeeded:
          //   - success: the backend rotated the cookies on the refresh
          //     response, so withCredentials:true sends the fresh `token`.
          //   - failure: another tab sharing the cookie jar may have rotated
          //     the refresh token first (the backend answers the loser of
          //     that race with a plain 401 and keeps the session — AUTH-9
          //     grace window). The jar then already holds that tab's fresh
          //     tokens, so the replay succeeds. If the session really is
          //     dead, the replay (marked _authRetry) 401s through this same
          //     interceptor, which runs the normal 401 handling below once.
          await this.tryRefresh()
          return this.client.request(cfg)
        }

        // Log error response (except health checks)
        if (!this.shouldIgnoreRequest(error.config)) {
          logger.logError(
            error.config?.method?.toUpperCase() || 'GET',
            error.config?.url || '',
            error,
            duration,
            requestId,
            {
              statusCode: error.response?.status,
              statusText: error.response?.statusText,
            }
          )
        }

        const toasted = this.handleErrorResponse(error)

        // #1396 -- the interceptor used to reject with the response BODY only
        // (`error.response?.data || error`), so `.status`, `.code`, etc. never
        // survived past this point. Callers throughout the app had no reliable
        // way to branch on HTTP status and fell back to defensively probing
        // half a dozen shapes (`err.status`, `err.response?.status`,
        // `err.statusCode`, message string-matching...) none of which
        // actually existed. Stamp the real HTTP status (and code, if the body
        // didn't already carry one) onto the rejected payload so every caller
        // can just read `err.status` / `err.code`.
        //
        // No response at all (network error, timeout, CORS) — nothing to
        // stamp a status onto; reject with the original error unchanged, same
        // as before this fix.
        if (!error.response) {
          return Promise.reject(error)
        }

        // Body isn't guaranteed to be an object (a non-JSON error page, an
        // empty body, a raw string) — never mutate a primitive in place.
        const body = error.response.data
        const payload: ApiErrorPayload =
          body && typeof body === 'object'
            ? body
            : { message: typeof body === 'string' && body ? body : undefined }
        payload.status = error.response.status
        payload.code = payload.code ?? (body && typeof body === 'object' ? body.code : undefined)
        // #2208: tell handlers a toast is already up (apiClientToasted), so
        // they do not show the same error a second time.
        payload.toasted = toasted
        return Promise.reject(payload)
      }
    )
  }

  /**
   * Handle error responses with appropriate user feedback. Returns true when
   * it showed a toast (#2208: stamped on the rejection as `toasted`).
   */
  private handleErrorResponse(error: any): boolean {
    if (error.response?.status === 401) {
      // 401 = Unauthorized. Always treat as auth failure: drop any stale
      // tokens and push the user to /login. Silent (no toast) to avoid
      // "permission denied" noise on pages that load with an expired
      // cookie. Skip the redirect on public/auth pages to avoid loops.
      //
      // Cookies.remove WITHOUT { path: '/' } only clears the cookie at
      // the current page path — the backend sets `token` and
      // `refresh_token` at Path=/, so the bare remove() leaves them
      // intact. Without the explicit path, a stale JWT signed by a
      // rotated JWT_SECRET keeps getting sent on every subsequent
      // request and OAuth login appears to be broken even though the
      // server set fresh cookies on /auth/callback. Same fix for the
      // refresh_token sibling.
      Cookies.remove('token', { path: '/' })
      Cookies.remove('refresh_token', { path: '/' })
      // localStorage carried `accessToken`/`refreshToken`/`user` from
      // the pre-cookie auth era. AuthContext still seeds itself from
      // these on boot, so leaving them behind would re-hydrate an
      // expired session after a 401-driven redirect. Wipe alongside
      // the cookies so the user actually lands logged-out.
      try {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
      } catch { /* SSR / private-mode safety */ }
      const publicPaths = [
        '/welcome',
        '/login',
        '/forgot-password',
        // #1208 — the reset-password landing page is reached logged-out
        // via the email link; without this exclusion the bootstrap 401
        // bounces the visitor to /login before they can set a password.
        '/reset-password',
        '/register',
        '/onboarding',
        // #592 fix — token-driven pages a logged-OUT visitor reaches via a
        // magic link. The app's bootstrap user-fetch 401s (no session), and
        // without these exclusions the global redirect bounced the visitor to
        // /login before they could act — even though the page's own ?token=
        // calls succeed. `/onboarding-status` covers
        // `/onboarding-status/contract` (the contract sign link).
        '/onboarding-status',
        '/accept-invite',
        '/verify-email',
        '/auth/complete-account',
        '/privacy-policy',
        '/terms-of-service',
        '/cookie-policy',
        // Legal/compliance pages, email unsubscribe and public profiles are
        // reachable logged-out (App.tsx mounts them outside ProtectedRoute).
        '/dpa',
        '/subprocessors',
        '/unsubscribe',
        '/profile',
        '/auth/callback',
        // #284 — same reasoning as the #592 block above, one degree worse.
        // /embed/work-with-us/:token is rendered INSIDE a hotel's iframe, where
        // the SameSite session cookie is never sent, so AuthProvider's
        // bootstrap profile fetch always 401s. Without this exclusion the
        // global redirect replaced the hotel's embedded widget with /login.
        // /apply/:hotelId is its top-level companion: it does its own
        // <Navigate to="/login" state={{ from }} /> so the return path
        // survives, which the blanket window.location.href would discard.
        '/embed',
        '/apply',
        // #1499 / #1646 — public affiliate-program info page, reached logged-out
        // from marketing links; the bootstrap 401 must not bounce it to /login.
        '/affinfo',
      ]
      const currentPath = window.location.pathname
      const onPublicPage = publicPaths.some(
        p => currentPath === p || currentPath.startsWith(p + '/')
      )
      if (!onPublicPage) {
        window.location.href = '/login'
      }
    } else if ((error as { config?: ExtendedAxiosRequestConfig }).config?.skipErrorToast) {
      // #2209: the caller shows its own error (see ExtendedAxiosRequestConfig).
      return false
    } else if (error.response?.status === 403 && error.response?.data?.code === 'FORCE_PASSWORD_CHANGE') {
      // Emit custom event for force password change
      window.dispatchEvent(new CustomEvent('api-error', {
        detail: {
          status: error.response.status,
          code: error.response.data.code,
          message: error.response.data.error,
          details: error.response.data.details
        }
      }))
    } else if (error.response?.status === 403) {
      // Stable id so concurrent 403s collapse into a single toast.
      toast.error(
        i18n.t('common:errors.noPermission', 'You do not have permission to perform this action.'),
        { id: 'api-403' },
      )
      return true
    } else if (error.response?.status === 400) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Invalid request.'
      toast.error(msg)
      return true
    } else if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
      // #788: 502/503/504 mean an upstream service is unreachable or degraded
      // (every pod rollout emits these transiently). The original design left
      // this silent, assuming each page renders its own error panel + Retry —
      // but in practice almost no page does, so a failure renders as convincing
      // fake data (₪0 earnings, "No hotels found"). Surface a toast until
      // per-page error panels exist, so a transient outage is never mistaken
      // for real data. Stable id collapses rollout bursts into one toast.
      toast.error(
        i18n.t('common:errors.serviceUnavailable', 'A service is temporarily unavailable. Please try again in a moment.'),
        { id: 'api-5xx' },
      )
      return true
    } else if (error.response?.status >= 500) {
      // 500 (real backend exception): keep the toast. Stable id so
      // bursts during pod rollouts collapse into a single toast.
      toast.error('Server error. Please try again later.', { id: 'api-5xx' })
      return true
    }
    return false
  }

  /**
   * Determines if a request should be ignored for logging purposes
   */
  private shouldIgnoreRequest(config: ExtendedAxiosRequestConfig): boolean {
    if (!config) return false

    const url = config.url || ''
    const userAgent = config.headers?.['User-Agent'] || ''
    const method = config.method?.toUpperCase() || ''

    // Ignore Kubernetes probes
    if (userAgent.includes('kube-probe') || userAgent.includes('kubectl')) {
      return true
    }

    // Ignore health check endpoints
    for (const path of HEALTH_CHECK_PATHS) {
      if (url.includes(path)) {
        return true
      }
    }

    // Ignore root path GET requests (load balancer health checks)
    if (method === 'GET' && (url === '/' || url === '')) {
      return true
    }

    // Ignore internal user agents
    for (const agent of INTERNAL_USER_AGENTS) {
      if (userAgent.toLowerCase().includes(agent.toLowerCase())) {
        return true
      }
    }

    return false
  }

  /**
   * HTTP GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.client.get(url, config)
  }

  /**
   * HTTP POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.client.post(url, data, config)
  }

  /**
   * HTTP PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.client.put(url, data, config)
  }

  /**
   * HTTP PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.client.patch(url, data, config)
  }

  /**
   * HTTP DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.client.delete(url, config)
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
export default apiClient
